export function getIncludedTypes(vibes: string[], fallbackLevel: number = 0): string[] {
  const typeSet = new Set<string>();

  vibes.forEach(vibe => {
    const types = getTypesForVibe(vibe, fallbackLevel);
    types.forEach(type => typeSet.add(type));
  });

  return Array.from(typeSet);
}

function getTypesForVibe(vibe: string, fallbackLevel: number): string[] {
  switch (vibe) {
    case 'hungry':
      if (fallbackLevel === 0) {
        return ['restaurant', 'cafe', 'bakery', 'bar', 'meal_takeaway', 'meal_delivery'];
      } else if (fallbackLevel === 1) {
        return ['restaurant', 'cafe', 'fast_food_restaurant', 'coffee_shop'];
      } else if (fallbackLevel === 2) {
        return ['restaurant', 'cafe', 'bar'];
      } else if (fallbackLevel === 3) {
        return ['restaurant'];
      } else {
        return ['restaurant', 'cafe'];
      }

    case 'must-sees':
      if (fallbackLevel === 0) {
        return ['tourist_attraction', 'museum', 'art_gallery', 'church', 'hindu_temple', 'mosque', 'synagogue', 'park', 'amusement_park', 'aquarium', 'zoo', 'performing_arts_theater'];
      } else if (fallbackLevel === 1) {
        return ['tourist_attraction', 'museum', 'park', 'cultural_center'];
      } else if (fallbackLevel === 2) {
        return ['tourist_attraction', 'park', 'museum'];
      } else if (fallbackLevel === 3) {
        return ['tourist_attraction', 'park'];
      } else {
        return ['tourist_attraction'];
      }

    case 'adventurous':
      if (fallbackLevel === 0) {
        return ['park', 'national_park', 'campground', 'hiking_area', 'marina', 'amusement_park', 'aquarium', 'zoo', 'sports_club', 'sports_complex', 'stadium', 'ski_resort', 'tourist_attraction', 'athletic_field', 'bowling_alley'];
      } else if (fallbackLevel === 1) {
        return ['amusement_park', 'aquarium', 'zoo', 'tourist_attraction', 'park', 'marina'];
      } else if (fallbackLevel === 2) {
        return ['tourist_attraction', 'amusement_park', 'park'];
      } else if (fallbackLevel === 3) {
        return ['tourist_attraction', 'park'];
      } else {
        return ['tourist_attraction'];
      }

    case 'hidden-gems':
      if (fallbackLevel === 0) {
        return ['bar', 'cafe', 'book_store', 'art_gallery', 'night_club', 'liquor_store', 'market'];
      } else if (fallbackLevel === 1) {
        return ['bar', 'cafe', 'restaurant', 'shopping_mall'];
      } else if (fallbackLevel === 2) {
        return ['bar', 'cafe', 'restaurant'];
      } else if (fallbackLevel === 3) {
        return ['restaurant', 'cafe'];
      } else {
        return ['cafe', 'bar'];
      }

    case 'popular':
      if (fallbackLevel === 0) {
        return ['tourist_attraction', 'restaurant', 'shopping_mall', 'museum', 'park', 'art_gallery'];
      } else if (fallbackLevel === 1) {
        return ['tourist_attraction', 'restaurant', 'park', 'museum'];
      } else if (fallbackLevel === 2) {
        return ['tourist_attraction', 'park', 'restaurant'];
      } else if (fallbackLevel === 3) {
        return ['tourist_attraction', 'park'];
      } else {
        return ['tourist_attraction'];
      }

    case 'feeling-lucky':
      if (fallbackLevel === 0) {
        return ['night_club', 'casino', 'spa', 'movie_theater', 'bowling_alley', 'amusement_park', 'bar'];
      } else if (fallbackLevel === 1) {
        return ['bar', 'restaurant', 'cafe', 'amusement_park', 'tourist_attraction'];
      } else if (fallbackLevel === 2) {
        return ['amusement_park', 'tourist_attraction', 'bar'];
      } else if (fallbackLevel === 3) {
        return ['tourist_attraction', 'bar'];
      } else {
        return ['tourist_attraction'];
      }

    default:
      return [];
  }
}

export function getSearchStrategy(vibes: string[]): {
  rankPreference?: string;
} {
  // Use POPULARITY ranking for vibes that want famous/main attractions
  if (
    vibes.includes('popular') ||
    vibes.includes('must-sees') ||
    vibes.includes('adventurous')
  ) {
    return { rankPreference: 'POPULARITY' };
  }

  // Use DISTANCE for more local/intimate vibes
  return { rankPreference: 'DISTANCE' };
}

export function getSearchRadius(transport: string, timeAvailable: string): number {
  const isHalfDay = timeAvailable.includes('half');
  const isFullDay = timeAvailable.includes('full');
  const isMultiDay = timeAvailable.includes('2-3');

  if (transport === 'walking') {
    if (isMultiDay) return 15000;
    if (isFullDay) return 10000;
    if (isHalfDay) return 5000;
    return 2500;
  }

  if (transport === 'transit') {
    if (isMultiDay) return 80000;
    if (isFullDay) return 60000;
    if (isHalfDay) return 35000;
    return 15000;
  }

  if (transport === 'taxi') {
    if (isMultiDay) return 250000;
    if (isFullDay) return 180000;
    if (isHalfDay) return 100000;
    return 40000;
  }

  return 5000;
}

export function getTextSearchQuery(vibe: string, location: string): string {
  const queries: Record<string, string> = {
    'hungry': `best restaurants and cafes in ${location}`,
    'must-sees': `must-see attractions and landmarks in ${location}`,
    'adventurous': `outdoor adventures and unique activities in ${location}`,
    'hidden-gems': `hidden gem local favorites in ${location}`,
    'popular': `most popular places to visit in ${location}`,
    'feeling-lucky': `fun entertainment and nightlife in ${location}`,
  };
  return queries[vibe] || `things to do in ${location}`;
}

export function getExcludedTypesForVibe(
  vibe: string,
  otherVibes: string[],
  categorySettings?: Record<string, string[]>
): string[] {
  if (otherVibes.length === 0) return [];

  const myTypes = new Set(
    categorySettings?.[vibe] || getTypesForVibe(vibe, 0)
  );

  const otherTypes = new Set<string>();
  for (const other of otherVibes) {
    const types = categorySettings?.[other] || getTypesForVibe(other, 0);
    types.forEach(t => {
      if (!myTypes.has(t)) otherTypes.add(t);
    });
  }

  return Array.from(otherTypes);
}

export function estimateTravelTime(
  distanceMeters: number,
  transport: string
): number {
  const walkingSpeedMph = 3.1;
  const transitSpeedMph = 15;
  const taxiSpeedMph = 25;

  const distanceMiles = distanceMeters / 1609.34;

  let travelHours: number;
  if (transport === 'walking') {
    travelHours = distanceMiles / walkingSpeedMph;
  } else if (transport === 'transit') {
    travelHours = distanceMiles / transitSpeedMph + 0.15;
  } else if (transport === 'taxi') {
    travelHours = distanceMiles / taxiSpeedMph;
  } else {
    travelHours = distanceMiles / walkingSpeedMph;
  }

  return Math.round(travelHours * 60);
}
